import React, { useEffect, useRef, useState } from "react";
import cn from "clsx";
import isArray from "lodash/isArray";
import isEqual from "lodash/isEqual";
import get from "lodash/get";
import { useTranslation } from "react-i18next";

import DropdownList from "./dropdownList";
import DropdownLabel from "./dropdownLabel";
import { useIsRTL } from "@/hooks";
import { DROP_DOWN_SCROLL_TYPES } from "@/constants";
import { SearchInput } from "../common/select/searchInput";
import { DropdownOptionProps, DropdownProps } from "./type";

import "./style.scss";

const CustomDropdown = ({
  data,
  handleChange,
  disableOption,
  readonly,
  containerClass,
  selectedItem,
  scrollbarMaxHeight,
  itemHeight,
  dropdownClass,
  scrollType,
  withSearchInput,
  noDataContent,
  ...rest
}: DropdownProps) => {
  const [isToggle, setIsToggle] = useState<boolean>(false);
  const [filteredData, setFilteredData] = useState<Array<DropdownOptionProps>>(
    []
  );
  const [searchInputValue, setSearchInputValue] = useState("");

  const isRTL = useIsRTL();
  const { t } = useTranslation("checkout");

  const wrapperRef = useRef(null);

  useEffect(() => {
    handleScroll();
    if (isArray(data)) {
      setFilteredData([...data]);
    }
    return () => {
      closeDropdown();
    };
  }, []);

  useEffect(() => {
    if (Array.isArray(data) && !isEqual(filteredData, data)) {
      setFilteredData([...data]);
    }
  }, [data]);

  const handleScroll = () => {
    if (scrollType === DROP_DOWN_SCROLL_TYPES.DROP_DOWN_WITHOUT_INLINE_SCROLL) {
      document.addEventListener("mousedown", handleClickOutside);
      return;
    }

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("scroll", closeDropdown);
  };

  const handleClickOutside = e => {
    const refWrapper = wrapperRef && !wrapperRef.current?.contains(e.target);

    if (!isToggle && refWrapper) {
      e.stopPropagation();
      setIsToggle(false);
      withSearchInput && resetSearchInputData();
    }
  };

  const closeDropdown = () => {
    if (isToggle) {
      setIsToggle(false);
      withSearchInput && resetSearchInputData();
    }
  };

  const handleOnChange = (item: any) => {
    const isDisabled =
      get(item, "labelElement.props.isDisabled", "") ||
      get(item, "labelElement.disableOption", "");

    if (disableOption === true && isDisabled === true) {
      setTimeout(() => {
        setIsToggle(true);
      }, 50);
      return false;
    }

    setTimeout(() => {
      setIsToggle(false);
    }, 150);
    withSearchInput && resetSearchInputData();

    handleChange(item);
  };

  const handleDropdownToggle = e => {
    e.stopPropagation();

    if (data?.length === 0) {
      return false;
    }

    setIsToggle(toggle => !toggle);
    !isToggle && withSearchInput && resetSearchInputData();
  };

  const resetSearchInputData = () => {
    if (data) {
      setSearchInputValue("");
      setFilteredData([...data]);
    }
  };

  const onSearchValueChange = value => {
    setSearchInputValue(value);
    const filteredData = data.filter(item =>
      item.label.toString().toLowerCase().includes(value.toLowerCase())
    );
    setFilteredData(filteredData);
  };

  return (
    <div
      ref={wrapperRef}
      className={cn(
        containerClass,
        readonly
          ? "read-only"
          : cn("dropdown_container", {
              clickable: data?.length > 0,
              zero_qty: !filteredData?.length,
              filter_open: isToggle
            }),
        { arabic: isRTL }
      )}
      onClick={handleDropdownToggle}
      {...rest}
    >
      <DropdownLabel data={data} item={selectedItem} readonly={readonly} />
      <div className="modalContainer">
        {isToggle && (
          <div className={"showModal"} onClick={e => e.stopPropagation()}>
            {readonly ? (
              <div>{selectedItem.label}</div>
            ) : (
              <>
                {withSearchInput && (
                  <SearchInput
                    searchValue={searchInputValue}
                    onSearchValueChange={onSearchValueChange}
                    placeholder={t("typeHere")}
                  />
                )}
                <DropdownList
                  data={filteredData}
                  selectedValue={selectedItem}
                  handleChange={handleOnChange}
                  className={dropdownClass}
                  scrollbarMaxHeight={scrollbarMaxHeight}
                  itemHeight={itemHeight}
                  noDataContent={noDataContent}
                />
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomDropdown;
